import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AuthState, LoginResponse } from "types";

const TOKEN_KEY = "bridge13_token";

const stored = localStorage.getItem(TOKEN_KEY);
let initialState: AuthState = {
  token:           null,
  role:            null,
  userId:          null,
  userName:        null,
  partnerEntityId: null,
  impersonating:   false,
  adminSnapshot:   null,
};

// Rehydrate from localStorage on page load (impersonation fields are never in localStorage)
if (stored) {
  try {
    const parsed = JSON.parse(stored) as Omit<AuthState, "impersonating" | "adminSnapshot">;
    initialState = { ...initialState, ...parsed };
  } catch {
    localStorage.removeItem(TOKEN_KEY);
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials(state, action: PayloadAction<LoginResponse>) {
      const { token, role, user } = action.payload;
      state.token           = token;
      state.role            = role;
      state.userId          = user.id;
      state.userName        = user.name;
      state.partnerEntityId = user.partner_entity_id;
      // Persist only the base credentials, never impersonation state
      localStorage.setItem(TOKEN_KEY, JSON.stringify({
        token:           state.token,
        role:            state.role,
        userId:          state.userId,
        userName:        state.userName,
        partnerEntityId: state.partnerEntityId,
      }));
    },
    logout(state) {
      state.token           = null;
      state.role            = null;
      state.userId          = null;
      state.userName        = null;
      state.partnerEntityId = null;
      state.impersonating   = false;
      state.adminSnapshot   = null;
      localStorage.removeItem(TOKEN_KEY);
    },
    startImpersonation(state, action: PayloadAction<LoginResponse>) {
      // Save current credentials so we can restore them later
      state.adminSnapshot = {
        token:           state.token!,
        role:            state.role!,
        userId:          state.userId!,
        userName:        state.userName!,
        partnerEntityId: state.partnerEntityId,
      };
      // Switch to the impersonated user — NOT written to localStorage
      const { token, role, user } = action.payload;
      state.token           = token;
      state.role            = role;
      state.userId          = user.id;
      state.userName        = user.name;
      state.partnerEntityId = user.partner_entity_id;
      state.impersonating   = true;
    },
    stopImpersonation(state) {
      if (!state.adminSnapshot) return;
      state.token           = state.adminSnapshot.token;
      state.role            = state.adminSnapshot.role;
      state.userId          = state.adminSnapshot.userId;
      state.userName        = state.adminSnapshot.userName;
      state.partnerEntityId = state.adminSnapshot.partnerEntityId;
      state.adminSnapshot   = null;
      state.impersonating   = false;
    },
  },
});

export const { setCredentials, logout, startImpersonation, stopImpersonation } = authSlice.actions;
export default authSlice.reducer;
